import * as React from 'react'
import debounce from 'lodash/debounce'
import {useLocation, useNavigate} from 'react-router'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type Column
} from '@tanstack/react-table'

import {createDatatableUrl} from '@frontrow/console/src/helpers/data-table'
import {INPUT_BASE} from '@frontrow/console/src/lib/form-styles'
import {cn} from '@frontrow/console/src/lib/utils'

export type FilterT = {
  id: string
  value: string
}

export type FetchDataParamsT = {
  filters: Array<FilterT>
}

export type FilterArgsT = {
  filter: FilterT
  onChange: (value: string) => void
}

export type DataTableColumnMeta = {
  Filter?: (args: FilterArgsT) => React.ReactElement
  dataTest?: string
}

type Props<T> = {
  columns: ColumnDef<T, unknown>[]
  data: Array<T>
  isLoading: boolean
  fetchData?: (params: FetchDataParamsT) => void
  filterable?: boolean
  manualFiltering?: boolean
  defaultPageSize?: number
  defaultSorted?: SortingState
  defaultFiltered?: Array<FilterT>
  updateUrlOnFilter?: boolean
  onFilteredChange?: (filters: Array<FilterT>) => void
}

function renderFilterUI<T>(
  column: Column<T>,
  columnFilters: ColumnFiltersState,
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
): React.ReactNode {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined
  const currentFilter = columnFilters.find(f => f.id === column.id)
  const currentValue = String(currentFilter?.value ?? '')
  const onChange = (value: string) => {
    setColumnFilters(prev =>
      value === ''
        ? prev.filter(f => f.id !== column.id)
        : prev.some(f => f.id === column.id)
          ? prev.map(f => (f.id === column.id ? {id: column.id, value} : f))
          : [...prev, {id: column.id, value}]
    )
  }
  if (meta?.Filter) {
    return meta.Filter({
      filter: {id: column.id, value: currentValue},
      onChange
    })
  }
  return (
    <input
      type="text"
      className={INPUT_BASE}
      value={currentValue}
      onChange={e => onChange(e.target.value)}
      placeholder="Filter..."
    />
  )
}

const HEAD_CELL =
  'bg-[#faf9f6] px-2.5 py-2 text-left align-middle text-xs font-semibold' +
  ' uppercase tracking-[0.06em] text-[#8a857c]'

const SORTABLE_HEAD_CELL =
  'group/th cursor-pointer select-none hover:text-[#55524c]' +
  ' focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2'

const FILTER_CELL = 'bg-[#faf9f6] px-2.5 pt-0 pb-2 text-left align-middle font-normal'

const BODY_CELL =
  'px-2.5 py-2 text-left align-middle whitespace-nowrap' +
  ' border-b border-[#eeece7] group-hover/row:bg-[#faf9f6]'

// The last column holds the row actions; keep it reachable while the wider
// columns scroll underneath.
const PINNED_CELL = 'sticky right-0 shadow-[inset_1px_0_0_#e4e2dc,-6px_0_8px_-6px_rgb(0_0_0/0.18)]'

const HEAD_DIVIDER = 'border-b-[1.5px] border-[#e4e2dc]'

const MAX_SIZE_DEFAULT = Number.MAX_SAFE_INTEGER

function columnSizeStyle(maxSize: number | undefined): React.CSSProperties | undefined {
  if (maxSize === undefined || maxSize >= MAX_SIZE_DEFAULT) return undefined
  return {width: maxSize, maxWidth: maxSize}
}

export default function DataTable<T>(props: Props<T>): React.ReactElement {
  const {
    columns,
    data,
    fetchData,
    isLoading,
    manualFiltering = false,
    defaultPageSize = 20,
    defaultSorted,
    defaultFiltered,
    onFilteredChange,
    updateUrlOnFilter = false
  } = props

  const {pathname} = useLocation()
  const navigate = useNavigate()

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(() =>
    (defaultFiltered ?? []).map(f => ({id: f.id, value: f.value}))
  )

  const [sorting, setSorting] = React.useState<SortingState>(() => defaultSorted ?? [])

  // Stable refs so effects don't need to list callbacks as deps
  const fetchDataRef = React.useRef(fetchData)
  fetchDataRef.current = fetchData
  const onFilteredChangeRef = React.useRef(onFilteredChange)
  onFilteredChangeRef.current = onFilteredChange

  const table = useReactTable<T>({
    data,
    columns,
    state: {
      columnFilters,
      sorting,
      pagination: {pageIndex: 0, pageSize: defaultPageSize}
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: () => {},
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualFiltering,
    manualSorting: false,
    enableColumnFilters: props.filterable ?? false
  })

  // Debounced URL sync
  const debouncedUrlSync = React.useMemo(
    () =>
      debounce((filters: Array<FilterT>) => {
        if (updateUrlOnFilter) {
          const url = createDatatableUrl(pathname, {
            filters
          })
          // Keep the scroll position: the filters stay on the same page.
          navigate(url, {replace: true, preventScrollReset: true})
        }
      }, 300),
    [navigate, pathname, updateUrlOnFilter]
  )

  // The pending 300ms timer outlives the component, so without this a filter
  // change immediately before unmount navigates via navigate() after
  // the table is gone. Also cancels when the memo above is rebuilt.
  React.useEffect(() => {
    return () => {
      debouncedUrlSync.cancel()
    }
  }, [debouncedUrlSync])

  // Mirror filter state changes to fetchData + onFilteredChange + URL.
  // The mount call is intentional: callers initialise their fetch-params
  // state as null and rely on this effect firing on mount with the
  // defaultFiltered values to trigger the first real data load.
  React.useEffect(() => {
    const filters = columnFilters.map(f => ({
      id: f.id,
      value: String(f.value)
    }))
    if (fetchDataRef.current) {
      fetchDataRef.current({filters})
    }
    if (onFilteredChangeRef.current) {
      onFilteredChangeRef.current(filters)
    }
    debouncedUrlSync(filters)
  }, [columnFilters, debouncedUrlSync])

  const rows = table.getRowModel().rows

  return (
    <div>
      <p className="mb-2 text-sm text-muted-foreground" aria-live="polite">
        {isLoading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? 'result' : 'results'}`}
      </p>
      {/* Scrolling here rather than on the page is what lets the header stick. */}
      <div className="max-h-[calc(100vh-12rem)] overflow-auto rounded-md border border-[#e4e2dc] bg-white">
        <table
          data-test="content-table"
          className={cn(
            // `separate` (not `collapse`) so the sticky header keeps its border
            'w-full border-separate border-spacing-0 text-base',
            isLoading && 'opacity-50'
          )}
        >
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map(headerGroup => {
              const hasFilterRow = headerGroup.headers.some(h => h.column.getCanFilter())
              const lastIndex = headerGroup.headers.length - 1
              return (
                <React.Fragment key={headerGroup.id}>
                  <tr>
                    {headerGroup.headers.map((header, index) => {
                      const canSort = header.column.getCanSort()
                      const sorted = header.column.getIsSorted()
                      const toggleSorting = header.column.getToggleSortingHandler()
                      return (
                        <th
                          key={header.id}
                          className={cn(
                            HEAD_CELL,
                            canSort && SORTABLE_HEAD_CELL,
                            index === lastIndex && PINNED_CELL,
                            !hasFilterRow && HEAD_DIVIDER
                          )}
                          style={columnSizeStyle(header.column.columnDef.maxSize)}
                          aria-sort={canSort ? ariaSort(sorted) : undefined}
                          tabIndex={canSort ? 0 : undefined}
                          onClick={toggleSorting}
                          onKeyDown={
                            canSort
                              ? e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    toggleSorting?.(e)
                                  }
                                }
                              : undefined
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort ? (
                            <span
                              aria-hidden="true"
                              className={cn(
                                'ml-[0.35em] text-[0.7em]',
                                sorted ? 'text-primary' : 'opacity-30 group-hover/th:opacity-60'
                              )}
                            >
                              {sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : '⇅'}
                            </span>
                          ) : null}
                        </th>
                      )
                    })}
                  </tr>
                  {hasFilterRow && (
                    <tr>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          className={cn(
                            FILTER_CELL,
                            HEAD_DIVIDER,
                            index === lastIndex && PINNED_CELL
                          )}
                        >
                          {header.column.getCanFilter() ? (
                            <div
                              data-test={
                                (header.column.columnDef.meta as DataTableColumnMeta | undefined)
                                  ?.dataTest ?? undefined
                              }
                            >
                              {renderFilterUI(header.column, columnFilters, setColumnFilters)}
                            </div>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  className="px-2.5 py-10 text-center text-[#8a857c]"
                  colSpan={table.getVisibleFlatColumns().length}
                >
                  {isLoading ? 'Loading…' : 'No results match these filters.'}
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => {
                const cells = row.getVisibleCells()
                return (
                  <tr key={row.id} className="group/row">
                    {cells.map((cell, index) => (
                      <td
                        key={cell.id}
                        className={cn(
                          BODY_CELL,
                          rowIndex === rows.length - 1 && 'border-b-0',
                          index === cells.length - 1 && `${PINNED_CELL} bg-white`
                        )}
                        style={columnSizeStyle(cell.column.columnDef.maxSize)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ariaSort(sorted: false | 'asc' | 'desc'): 'ascending' | 'descending' | 'none' {
  if (sorted === 'asc') return 'ascending'
  if (sorted === 'desc') return 'descending'
  return 'none'
}
